import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import config from '../../lib/config'
import { useUserContext } from '../../contexts'
import { setSessionAuthorizationToken } from '../../lib/axiosInstance'
import { loginUserFn, providerLoginFn } from '../../lib/api'
import { useGoogleLogin } from '@react-oauth/google'
import { appleAuthHelpers } from 'react-apple-signin-auth'
import useFeature from '../../hooks/useFeature'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { deleteCookie, getCookie } from '../../lib/utils'
import { getLastLoginMethod, setLastLoginMethod } from '../../lib/lastLoginMethod'
import { Header } from '../Header'
import { Spinner } from '../Spinner'
import '../../styles/layout.scss'
import './Login.scss'
import { ButtonCTA } from '../Form'
import { getNativeAppClient, NativeAppHandoff } from '../NativeAppHandoff'
import {
  clearPortalLoginContinuation,
  openPortalWithRefreshToken,
  setPortalLoginContinuation,
} from '../../lib/portalLogin'

const EyeIcon = ({ open = false }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {open
      ? (
        <>
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/>
          <circle cx="12" cy="12" r="3"/>
        </>
      )
      : (
        <>
          <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-7 0-10-7-10-7a18.5 18.5 0 0 1 5.07-5.94"/>
          <path d="M9.9 4.24A10.36 10.36 0 0 1 12 4c7 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19"/>
          <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88"/>
          <line x1="2" y1="2" x2="22" y2="22"/>
        </>
      )}
  </svg>
)

const AppleIcon = () => (
  <svg className="social-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
  </svg>
)

const Login = ({ remoteCallback, standalone, closeCallback }) => {
  //Refs
  const headerRef = useRef(null)
  const reCaptchaRef = useRef(null)
  const portalAppleAutoAttemptedRef = useRef(false)
  const portalHandoffStartedRef = useRef(false)

  //Custom Hooks
  const { user, isAuthenticating, setUserToken, setRefreshToken, setFocusMode, replaceUserSession } = useUserContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const seatRevokedReason = searchParams.get('reason') === 'seat-revoked'
  const portalNoSeatsError = remoteCallback === 'portal' && searchParams.get('error') === 'no_seats_available'
  const isPortalAppleLogin = searchParams.get('origin') === 'portal' && searchParams.get('provider') === 'apple'
  const nativeAppClient = getNativeAppClient(searchParams)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showLoginForm, setShowLoginForm] = useState(true)
  const [showRedirectToApp, setShowRedirectToApp] = useState(false)
  const [socialLoading, setSocialLoading] = useState(false)
  const [emailLoginLoading, setEmailLoginLoading] = useState(false)
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [emailCallbackToken, setEmailCallbackToken] = useState(null)
  const [emailCallbackError, setEmailCallbackError] = useState(null)
  const [emailCallbackWaitingForAuth, setEmailCallbackWaitingForAuth] = useState(false)
  const [emailCallbackSawAuthenticating, setEmailCallbackSawAuthenticating] = useState(false)
  const [portalHandoffError, setPortalHandoffError] = useState(null)
  const [lastLoginMethod] = useState(getLastLoginMethod)

  const cleanEmailTokenFromUrl = () => {
    const currentUrl = new URL(window.location.href)
    if (!currentUrl.searchParams.has('token')) {
      return
    }
    currentUrl.searchParams.delete('token')
    const nextSearch = currentUrl.searchParams.toString()
    const nextUrl = `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ''}${currentUrl.hash}`
    window.history.replaceState({}, document.title, nextUrl)
  }

  const cleanPortalHandoffFromUrl = () => {
    const currentUrl = new URL(window.location.href)
    ;['web_access_token', 'web_refresh_token', 'provider', 'portal_login_expires_at'].forEach((key) => {
      currentUrl.searchParams.delete(key)
    })
    const nextSearch = currentUrl.searchParams.toString()
    window.history.replaceState({}, document.title, `${currentUrl.pathname}${nextSearch ? `?${nextSearch}` : ''}${currentUrl.hash}`)
  }

  const runEmailCallbackLogin = (tokenValue) => {
    if (!tokenValue) {
      setEmailCallbackWaitingForAuth(false)
      setEmailCallbackSawAuthenticating(false)
      setEmailCallbackError('Verification token is missing. Please log in to continue.')
      setLoading(false)
      setShowLoginForm(false)
      return
    }
    setEmailCallbackToken(tokenValue)
    setEmailCallbackError(null)
    setEmailCallbackWaitingForAuth(true)
    setEmailCallbackSawAuthenticating(false)
    setShowLoginForm(false)
    setLoading(true)
    setTimeout(() => successCallback(tokenValue), 1000)
  }

  //UseEffect callbacks
  useEffect(() => {
    if (remoteCallback) {
      setLoading(true)
      if (remoteCallback === 'email') {
        const tokenResponse = searchParams.get('token')
        if (!tokenResponse) {
          setEmailCallbackWaitingForAuth(false)
          setEmailCallbackSawAuthenticating(false)
          setEmailCallbackError('Verification token is missing. Please log in to continue.')
          setShowLoginForm(false)
          setLoading(false)
          return
        }
        cleanEmailTokenFromUrl()
        runEmailCallbackLogin(tokenResponse)
        return
      }

      let tokenResponse = searchParams.get('token')

      if (tokenResponse) {
        switch (remoteCallback) {
          case 'google':
            setTimeout(() => oAuthSuccessCallback('google', { access_token: decodeURIComponent(tokenResponse) }), 1000)
            break
          case 'facebook':
            setTimeout(() => oAuthSuccessCallback('facebook', { access_token: decodeURIComponent(tokenResponse) }), 1000)
            break
        }
        return
      }

      let refreshTokenResponse = searchParams.get('refresh_token')
      if (refreshTokenResponse) {
        switch (remoteCallback) {
          case 'portal':
            setFocusMode(true)
            setShowLoginForm(false)
            openPortalWithRefreshToken(refreshTokenResponse, (portalUrl) => {
              setLoading(false)
              setShowRedirectToApp(portalUrl)
            })
            break
        }

        return
      }
      return
    }
  }, [])

  useEffect(() => {
    if (!portalNoSeatsError) {
      return
    }
    if (portalHandoffStartedRef.current) {
      return
    }

    portalHandoffStartedRef.current = true

    const accessToken = searchParams.get('web_access_token')
    const refreshToken = searchParams.get('web_refresh_token')
    const provider = searchParams.get('provider')
    const expiresAt = searchParams.get('portal_login_expires_at')
    cleanPortalHandoffFromUrl()
    setShowLoginForm(false)

    if (!accessToken || !refreshToken || !['google', 'apple'].includes(provider) || !expiresAt) {
      clearPortalLoginContinuation()
      navigate('/account/seats/manage?reason=no-seats', { replace: true })
      return
    }

    let active = true
    setLoading(true)
    replaceUserSession(accessToken, refreshToken)
      .then(() => {
        if (!active) return
        setPortalLoginContinuation({ provider, expiresAt })
        navigate('/account/seats/manage?reason=no-seats&source=portal', { replace: true })
      })
      .catch(() => {
        if (!active) return
        clearPortalLoginContinuation()
        setPortalHandoffError('Unable to load the account signed in through Portal. Return to Portal and try again.')
        setLoading(false)
      })

    return () => {
      active = false
    }
  }, [navigate, portalNoSeatsError, replaceUserSession, searchParams])

  useEffect(() => {
    if (portalNoSeatsError) {
      return
    }

    if (!user) {
      if (remoteCallback === 'email') {
        setShowLoginForm(false)

        if (emailCallbackError) {
          setLoading(false)
          return
        }

        if (!emailCallbackWaitingForAuth) {
          setLoading(false)
          return
        }

        if (isAuthenticating) {
          if (!emailCallbackSawAuthenticating) {
            setEmailCallbackSawAuthenticating(true)
          }
          setLoading(true)
          return
        }

        if (emailCallbackSawAuthenticating) {
          setEmailCallbackWaitingForAuth(false)
          setLoading(false)
          setEmailCallbackError('This verification link is invalid or expired. Please try again or log in.')
          return
        }

        setLoading(true)
        return
      }

      setLoading(!!remoteCallback || isAuthenticating)
      return
    }
    if (standalone) {
      return
    }

    if (nativeAppClient) {
      setShowLoginForm(false)
      setLoading(false)
      return
    }

    if (remoteCallback === 'email') {
      setLastLoginMethod('email')
    }

    // Check for crateLoginRedirect cookie
    const crateLoginRedirect = getCookie('crateLoginRedirect')
    if (crateLoginRedirect) {
      // Updated regex to allow URLs with or without a URI path
      deleteCookie('crateLoginRedirect')
      if (typeof crateLoginRedirect === 'string' && /^(https:\/\/([\w-]+\.)*productioncrate\.com(\/.*)?)$|^\/[^\s]*$/.test(crateLoginRedirect)) {

        // Use document.location.href for absolute URLs; navigate for relative paths
        if (crateLoginRedirect.startsWith('https://')) {
          document.location.href = crateLoginRedirect
        } else {
          navigate(crateLoginRedirect, { replace: true })
        }
        return
      }
    } else {
      // Check for redirect_uri and optional redirect_sub query parameters
      const redirectUri = searchParams.get('redirect_uri')
      const redirectSub = searchParams.get('redirect_sub') || ''

      if (redirectUri && redirectUri.startsWith('/')) {
        const redirectUrl = redirectSub
          ? `https://${redirectSub}.productioncrate.com${redirectUri}`
          : `https://productioncrate.com${redirectUri}`

        document.location.href = redirectUrl // Use document.location.href for full redirection
        return
      }
    }

    // Default navigation if no redirection path is found
    navigate('/account', { replace: true })
  }, [
    user,
    isAuthenticating,
    remoteCallback,
    standalone,
    navigate,
    searchParams,
    emailCallbackError,
    emailCallbackWaitingForAuth,
    emailCallbackSawAuthenticating,
    portalNoSeatsError,
    nativeAppClient,
  ])

  //Handlers:
  const handleEmailLogin = async () => {
    if (emailLoginLoading || socialLoading) {
      return
    }

    /*
    let recaptchaToken;
    try{
        recaptchaToken = await reCaptchaRef.current.executeAsync();

        console.log("RECAPTCHA token", recaptchaToken);
    }catch(e){
        console.log("RECAPTCHA ERROR", e);
        return;
    }
    */
    let data = {
      'password': password,
      //'g-recaptcha-response': recaptchaToken,
    }

    if (username.includes('@')) {
      data.email = username
    } else {
      data.username = username
    }

    setEmailLoginLoading(true)

    try {
      const { access_token, refresh_token } = await loginUserFn(data)
      successCallback(access_token, refresh_token, 'email')

    } catch (res) {
      //console.log("log in failed", res.error?res.error:res);
      if (res.error && res.error == 'Unauthorized') {
        setError('User or password incorrect')
      }
    } finally {
      setEmailLoginLoading(false)
    }

  }

  const successCallback = useCallback((token, refreshToken, loginMethod) => {
    setSessionAuthorizationToken(token)
    setUserToken(token)
    setRefreshToken(refreshToken)
    setLastLoginMethod(loginMethod)
  }, [setRefreshToken, setUserToken])

  const oAuthSuccessCallback = useCallback((provider, payloadResponse) => {
    const loginPayload = isPortalAppleLogin
      ? { ...payloadResponse, token_profile: 'desktop' }
      : payloadResponse

    providerLoginFn(provider, loginPayload)
      .then(({ access_token, refresh_token }) => {
        setLastLoginMethod(provider)
        if (isPortalAppleLogin) {
          window.location.assign(`/login/portal/callback?refresh_token=${encodeURIComponent(refresh_token)}`)
          return
        }
        successCallback(access_token, refresh_token)
      })
      .catch(() => {
        setError('Login failed. Please try again.')
      })
      .finally(() => {
        setSocialLoading(false)
      })
  }, [isPortalAppleLogin, successCallback])

  const googleLogin = useGoogleLogin({
    onSuccess: tokenResponse => oAuthSuccessCallback('google', tokenResponse),
    onError: () => {
      setError('Login failed. Please try again.')
      setSocialLoading(false)
    },
  })

  const handleGoogleLogin = () => {
    if (emailLoginLoading || socialLoading) {
      return
    }

    setError(null)
    setSocialLoading(true)
    googleLogin()
  }

  const appleLoginEnabled = useFeature('appleLogin')

  const handleAppleLogin = useCallback(async ({ automatic = false } = {}) => {
    if (emailLoginLoading || socialLoading) {
      return
    }

    setError(null)
    setSocialLoading(true)
    try {
      const response = await appleAuthHelpers.signIn({
        authOptions: {
          clientId: config.appleClientId,
          scope: 'email name',
          redirectURI: window.location.origin + '/login',
          usePopup: true,
        },
        onError: (error) => {
          if (error?.error === 'popup_closed_by_user') return
          console.error(error?.error ?? 'Apple OAuth Error')
        },
      })
      if (response) {
        console.debug('Apple Sign-In Response:', response)
        
        oAuthSuccessCallback('apple', {
          access_token: response.authorization.id_token,
          email: response.user?.email,
          first_name: response.user?.name?.firstName,
          last_name: response.user?.name?.lastName,
        })
      } else {
        setSocialLoading(false)
      }
    } catch (error) {
      setSocialLoading(false)
      if (error?.error === 'popup_closed_by_user') return
      if (!automatic) {
        setError('Login failed. Please try again.')
      }
    }
  }, [emailLoginLoading, oAuthSuccessCallback, socialLoading])

  useEffect(() => {
    if (!isPortalAppleLogin || portalAppleAutoAttemptedRef.current) {
      return
    }

    portalAppleAutoAttemptedRef.current = true
    handleAppleLogin({ automatic: true })
  }, [handleAppleLogin, isPortalAppleLogin])

  const handleFormSubmit = (event) => {
    event.preventDefault()
    handleEmailLogin()
  }

  const handleEmailCallbackRetry = () => {
    runEmailCallbackLogin(emailCallbackToken)
  }

  return (
    <div className="layout">
      {!standalone &&
        <Header ref={headerRef} minimal={isPortalAppleLogin || remoteCallback === 'portal'}/>
      }
      <div className="login">
        <>
          {loading &&
            <div className="empty-content">
              <Spinner/>
            </div>
          }
          {!loading && showLoginForm &&
            <form className="form" onClick={event => event.stopPropagation()} onSubmit={handleFormSubmit}>
              <div className="auth-card">
                <div className="title">{isPortalAppleLogin ? 'Continue to Portal' : 'Welcome back'}</div>
                <div className="subtitle">
                  {isPortalAppleLogin
                    ? 'Apple sign-in should open automatically. If it doesn’t, click Continue with Apple.'
                    : 'Access your ProductionCrate account.'}
                </div>

                <div className="social-actions">
                  {!isPortalAppleLogin && (
                    <button
                      type="button"
                      className={`social-btn social-btn-google${socialLoading ? ' social-loading' : ''}`}
                      disabled={socialLoading || emailLoginLoading}
                      onClick={handleGoogleLogin}
                    >
                      <img className="social-icon" src="/images/icon.google.svg" alt="google icon"/>
                      <span>Login with Google</span>
                      {lastLoginMethod === 'google' && <span className="last-used-chip">Last used</span>}
                    </button>
                  )}
                  {(appleLoginEnabled || isPortalAppleLogin) && (
                    <button
                      type="button"
                      className={`social-btn social-btn-apple${socialLoading ? ' social-loading' : ''}`}
                      disabled={socialLoading || emailLoginLoading}
                      onClick={() => handleAppleLogin()}
                    >
                      <AppleIcon />
                      <span>{isPortalAppleLogin ? 'Continue with Apple' : 'Login with Apple'}</span>
                      {lastLoginMethod === 'apple' && <span className="last-used-chip">Last used</span>}
                    </button>
                  )}
                </div>

                {isPortalAppleLogin && error && <div className="error-msg">{error}</div>}

                {!isPortalAppleLogin && (
                  <>
                    <div className="divider">
                      <span className="line"/>
                      <span className="label">OR USE EMAIL</span>
                      {lastLoginMethod === 'email' && <span className="last-used-chip">Last used</span>}
                      <span className="line"/>
                    </div>

                    {seatRevokedReason && (
                      <div className="session-msg">
                        Your seat was revoked by the team owner. Please log in again.
                      </div>
                    )}

                    <label className="field-block" htmlFor="login-username">
                      <span className="field-label">Username or Email</span>
                      <div className="field-control">
                        <input
                          id="login-username"
                          type="text"
                          placeholder="Enter your email or username"
                          onChange={({ target }) => {
                            setError(null)
                            setUsername(target.value)
                          }}
                          value={username}
                        />
                      </div>
                    </label>

                    <label className="field-block" htmlFor="login-password">
                      <span className="field-label">Password</span>
                      <div className="field-control">
                        <input
                          id="login-password"
                          type={passwordVisible ? 'text' : 'password'}
                          placeholder="Password"
                          onChange={({ target }) => {
                            setError(null)
                            setPassword(target.value)
                          }}
                          value={password}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setPasswordVisible(!passwordVisible)}
                          aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                        >
                          <EyeIcon open={passwordVisible}/>
                        </button>
                      </div>
                    </label>

                    <div
                      className="forgot-password"
                      onClick={() => navigate('/password-recovery', { replace: true })}
                    >
                      Forgot your password?
                    </div>

                    {error && <div className="error-msg">{error}</div>}

                    <button
                      type="submit"
                      className="login-cta-btn"
                      disabled={emailLoginLoading || socialLoading}
                    >
                      {emailLoginLoading ? 'Logging in...' : 'Login'}
                    </button>
                    <p className="signup-link">
                      Don’t have an account?{' '}
                      <a href={nativeAppClient ? `/register?native_app=${encodeURIComponent(nativeAppClient)}` : '/register'}>
                        Sign up
                      </a>
                    </p>
                  </>
                )}

                {standalone && closeCallback &&
                  <div
                    className="go-back-link"
                    onClick={() => closeCallback()}
                  >
                    Go Back
                  </div>
                }
              </div>
            </form>
          }
          {!loading && nativeAppClient && user && <NativeAppHandoff client={nativeAppClient} />}
          {!loading && portalHandoffError && (
            <div className="auth-card">
              <div className="title">Portal sign-in could not continue</div>
              <div className="subtitle">{portalHandoffError}</div>
              <a className="login-cta-btn callback-btn" href="productioncrate-portal://">
                Return to Portal
              </a>
            </div>
          )}
          {!loading && showRedirectToApp &&
            <div className="form fade-in">
              <div className="auth-card callback-card">
                <Spinner type="logo" loop={false} label={false} style={{ position: 'relative', margin: 'auto' }}/>
                <a className="login-cta-btn callback-btn" href={showRedirectToApp}>
                  Launch Portal
                </a>
                <div className="go-back-link" onClick={() => document.location.href = '/login'}>
                  Go Back
                </div>
              </div>
            </div>
          }
          {!loading && remoteCallback === 'email' && emailCallbackError &&
            <div className="form">
              <div className="auth-card callback-card">
                <div className="title callback-title">We could not complete your login</div>
                <div className="error-msg callback-error">{emailCallbackError}</div>
                {emailCallbackToken &&
                  <ButtonCTA label="Try again" onClick={handleEmailCallbackRetry} style={{ maxWidth: 'unset' }} />
                }
                <ButtonCTA
                  label="Go to login"
                  onClick={() => document.location.href = '/login'}
                  style={{ maxWidth: 'unset', marginTop: emailCallbackToken ? '12px' : '0' }}
                />
              </div>
            </div>
          }
        </>
        {false &&
          <ReCAPTCHA
            sitekey={config.recaptchaSiteKey}
            size="invisible"
            ref={reCaptchaRef}
          />
        }
      </div>
    </div>
  )
}

export default Login
